import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import * as crmBackend from '../services/crmBackendService';

/**
 * Process Payment — charge a customer's card live, right now, from staff hands.
 *
 * PCI: the card number / expiry / CVV entered below are handed straight to
 * Authorize.net's Accept.js in the browser (window.Accept.dispatchData) and are
 * exchanged for a single-use opaque nonce. Only that nonce is POSTed to our
 * backend. No raw card data ever reaches our servers, database or logs — this
 * is the same tokenization flow the customer portal's PaymentModal uses.
 *
 * This is deliberately SEPARATE from "Record Payment", which only logs a
 * payment already taken elsewhere (cash / check / Zelle / terminal). Both
 * exist side by side; neither replaces the other.
 *
 * Charging only ever happens on an explicit press of the Charge button.
 */
export default function ProcessPaymentDialog({ invoice, surface = 'Admin App', onClose, onSuccess }) {
  const [cfg, setCfg] = useState(null);
  const [cardNum, setCardNum] = useState('');
  const [expMonth, setExpMonth] = useState('');
  const [expYear, setExpYear] = useState('');
  const [cvv, setCvv] = useState('');
  const [status, setStatus] = useState('idle'); // idle | processing | success
  const [errorMsg, setErrorMsg] = useState('');
  const [result, setResult] = useState(null);

  const invoiceName = invoice?.name || '';
  // The backend charges whatever ERP reports as outstanding, so show that same
  // number rather than a possibly-stale total carried in from the calling page.
  const [outstanding, setOutstanding] = useState(
    parseFloat(invoice?.outstanding_amount ?? invoice?.grand_total ?? 0) || 0
  );
  const amountDue = outstanding;

  useEffect(() => {
    let cancelled = false;
    crmBackend.getPaymentClientConfig()
      .then((data) => { if (!cancelled) setCfg(data); })
      .catch(() => { if (!cancelled) setErrorMsg('Could not load payment configuration. Please try again.'); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!invoiceName) return undefined;
    let cancelled = false;
    crmBackend.getInvoiceDetail(invoiceName)
      .then((data) => {
        const inv = data?.data || data || {};
        const live = parseFloat(inv.outstanding_amount);
        if (!cancelled && Number.isFinite(live)) setOutstanding(live);
      })
      .catch(() => { /* keep the caller-supplied amount; the charge is server-authoritative */ });
    return () => { cancelled = true; };
  }, [invoiceName]);

  const handleCharge = () => {
    if (!window.Accept) {
      setErrorMsg('Payment processor failed to load. Refresh the page and try again.');
      return;
    }
    if (!cfg) {
      setErrorMsg('Payment configuration not loaded yet. Give it a moment and try again.');
      return;
    }
    const num = cardNum.replace(/\s/g, '');
    if (!num || !expMonth || !expYear || !cvv) {
      setErrorMsg('Enter the card number, expiry and CVV.');
      return;
    }
    if (!(amountDue > 0)) {
      setErrorMsg('This invoice has no outstanding balance to charge.');
      return;
    }
    setErrorMsg('');
    setStatus('processing');

    try {
      window.Accept.dispatchData(
        {
          authData: { apiLoginID: cfg.apiLoginID, clientKey: cfg.clientKey },
          cardData: { cardNumber: num, month: expMonth, year: expYear, cardCode: cvv },
        },
        async (response) => {
          try {
            if (response?.messages?.resultCode === 'Error') {
              const errs = (response.messages.message || []).map((m) => m.text).join(' ');
              setStatus('idle');
              setErrorMsg('Card error: ' + errs);
              return;
            }
            const od = response.opaqueData;
            // Clear the raw card fields from component state the moment we
            // have a token — they are no longer needed for anything.
            setCardNum(''); setExpMonth(''); setExpYear(''); setCvv('');
            const data = await crmBackend.chargeCard(invoiceName, {
              descriptor: od.dataDescriptor,
              value: od.dataValue,
              surface,
            });
            setResult(data);
            setStatus('success');
            if (onSuccess) onSuccess(data);
          } catch (e) {
            setStatus('idle');
            setErrorMsg(e.response?.data?.detail || 'Payment failed. Please try again.');
          }
        }
      );
    } catch (e) {
      setStatus('idle');
      setErrorMsg('Payment processor error: ' + (e.message || 'unknown error'));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-y-auto">
      <div className="bg-white w-full max-w-sm rounded-t-2xl sm:rounded-2xl p-6 space-y-4 my-auto">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-bold text-lg text-gray-900">Process Payment</h3>
            <p className="text-xs text-gray-500">
              Invoice {invoiceName}
              {invoice?.customer_name ? ` — ${invoice.customer_name}` : ''}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 text-2xl leading-none px-1" aria-label="Close">×</button>
        </div>

        {status === 'success' ? (
          <div className="text-center py-4 space-y-3">
            <div className="text-4xl">✅</div>
            <p className="font-semibold text-green-700">
              Charged ${amountDue.toFixed(2)}
            </p>
            {result?.trans_id && (
              <p className="text-xs text-gray-500">Authorize.net txn {result.trans_id}</p>
            )}
            <Button className="w-full h-12 bg-green-600 hover:bg-green-700 font-semibold" onClick={onClose}>
              Done
            </Button>
          </div>
        ) : (
          <>
            <div className="bg-gray-50 border rounded-lg px-3 py-2 text-center">
              <p className="text-xs text-gray-500">Amount to charge</p>
              <p className="text-2xl font-bold text-gray-900">${amountDue.toFixed(2)}</p>
            </div>

            {errorMsg && (
              <div className="bg-red-50 border border-red-200 rounded-md px-3 py-2 text-sm text-red-700">
                {errorMsg}
              </div>
            )}

            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Card Number</label>
              <Input
                type="tel" inputMode="numeric" maxLength={19} autoComplete="off"
                placeholder="•••• •••• •••• ••••"
                value={cardNum}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, '').substring(0, 16);
                  setCardNum(v.replace(/(.{4})/g, '$1 ').trim());
                }}
                className="h-12 text-lg"
              />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Month</label>
                <Input type="tel" maxLength={2} placeholder="MM" autoComplete="off" className="h-12 text-center"
                  value={expMonth} onChange={(e) => setExpMonth(e.target.value.replace(/\D/g, ''))} />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Year</label>
                <Input type="tel" maxLength={4} placeholder="YYYY" autoComplete="off" className="h-12 text-center"
                  value={expYear} onChange={(e) => setExpYear(e.target.value.replace(/\D/g, ''))} />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">CVV</label>
                <Input type="tel" maxLength={4} placeholder="•••" autoComplete="off" className="h-12 text-center"
                  value={cvv} onChange={(e) => setCvv(e.target.value.replace(/\D/g, ''))} />
              </div>
            </div>

            <div className="flex gap-3 pt-1">
              <Button variant="outline" className="flex-1 h-12" onClick={onClose} disabled={status === 'processing'}>
                Cancel
              </Button>
              <Button
                className="flex-1 h-12 bg-green-600 hover:bg-green-700 font-semibold"
                disabled={status === 'processing' || !cfg}
                onClick={handleCharge}
              >
                {status === 'processing' ? 'Charging…' : `Charge $${amountDue.toFixed(2)}`}
              </Button>
            </div>
            <p className="text-[11px] text-gray-400 text-center">
              🔒 Card details are encrypted by Authorize.net and never touch our servers
            </p>
          </>
        )}
      </div>
    </div>
  );
}
